import React from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import CustomModal from "../../../Global/Modal/CustomModal";
import {
  FaUserFriends,
  FaMapMarkerAlt,
  FaInfoCircle,
  FaCalendarAlt,
  FaMoneyCheckAlt,
  FaLink,
  FaChartLine,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
} from "react-icons/fa";
import { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import DataTable from "../../Robuts Components/DataTable";
import { FaStar } from "react-icons/fa";

const practiceDays = [
  { id: "ש", label: "יום שבת" },
  { id: "ו", label: "יום שישי" },
  { id: "ה", label: "יום חמישי" },
  { id: "ד", label: "יום רביעי" },
  { id: "ג", label: "יום שלישי" },
  { id: "ב", label: "יום שני" },
  { id: "א", label: "יום ראשון" },
];

// Custom smooth tooltip using portal for stacking context
const StatTooltip = ({ text, children, style }) => {
  const [show, setShow] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const iconRef = useRef(null);

  useEffect(() => {
    if (show && iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setCoords({
        top: rect.top + window.scrollY, // 40px above icon
        left: rect.left + window.scrollX + rect.width / 2,
      });
    }
  }, [show]);

  return (
    <span
      ref={iconRef}
      style={{ position: "absolute", ...style }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onClick={(e) => {
        e.stopPropagation();
        setShow((s) => !s);
      }}
    >
      {children}
      {show &&
        ReactDOM.createPortal(
          <span
            style={{
              position: "absolute",
              top: coords.top,
              left: coords.left,
              transform: "translate(-50%, -100%)",
              background: "#1976d2",
              color: "#fff",
              padding: "8px 14px",
              borderRadius: 8,
              fontSize: "0.98em",
              fontWeight: 500,
              whiteSpace: "nowrap",
              direction: "rtl",
              zIndex: 99999,
              boxShadow: "0 2px 8px #1976d233",
              pointerEvents: "none",
            }}
          >
            {text}
          </span>,
          document.body
        )}
    </span>
  );
};

function GroupDetailsModal({
  isOpen,
  onClose,
  group,
  students,
  onCopyLink,
  copyButtonState,
  onEdit,
  onDelete,
  hasEditPrivilege,
  userData,
  groups,
  presenceLogs,
}) {
  if (!group) return null;
  const groupStudents =
    students?.filter((student) =>
      Array.isArray(student.groupsIDs)
        ? student.groupsIDs.includes(group.id)
        : student.groupsIDs === group.id
    ) || [];
  const groupSize = groupStudents.length;

  // --- STATISTICS SECTION LOGIC ---
  // Filter presence logs for this group in the last 30 days
  const now = new Date();
  const thirtyDaysAgo = new Date(now);
  thirtyDaysAgo.setDate(now.getDate() - 30);
  const groupPresenceLogs = (presenceLogs || []).filter((log) => {
    if (log.groupID !== group.id) return false;
    let logTime;
    if (log.logTime && typeof log.logTime.toDate === "function") {
      logTime = log.logTime.toDate();
    } else if (log.logTime && log.logTime.seconds) {
      logTime = new Date(log.logTime.seconds * 1000);
    } else {
      logTime = log.logTime;
    }
    return logTime && logTime >= thirtyDaysAgo;
  });

  // 1) אימונים החודש
  const trainingsThisMonth = groupPresenceLogs.length;

  // 1.5) ספורטאים שהצטרפו (new students in last 30 days)
  const newStudentsCount = groupStudents.filter((student) => {
    if (!student.studentCreatedAt) return false;
    let createdAt;
    if (typeof student.studentCreatedAt?.toDate === "function") {
      createdAt = student.studentCreatedAt.toDate();
    } else if (student.studentCreatedAt?.seconds) {
      createdAt = new Date(student.studentCreatedAt.seconds * 1000);
    } else {
      createdAt = new Date(student.studentCreatedAt);
    }
    return createdAt && createdAt >= thirtyDaysAgo;
  }).length;

  // 2) אחוז נוכחות ממוצע באימונים
  let totalPresencePercent = 0;
  let validLogsCount = 0;
  const presencePercents = [];
  groupPresenceLogs.forEach((log) => {
    const presentMap = Array.isArray(log.presenceLogDataForFront)
      ? log.presenceLogDataForFront
      : [];
    // For each student in group, check if present
    let presentCount = 0;
    groupStudents.forEach((student) => {
      const entry = presentMap.find((e) => e.studentID === student.id);
      if (entry && entry.isPresent) presentCount++;
    });
    // If a student is not in the log, count as not present
    const percent =
      groupStudents.length > 0
        ? (presentCount / groupStudents.length) * 100
        : 0;
    if (!isNaN(percent)) {
      totalPresencePercent += percent;
      validLogsCount++;
      presencePercents.push(percent);
    }
  });
  const avgPresencePercent =
    validLogsCount > 0 ? Math.round(totalPresencePercent / validLogsCount) : 0;

  // 3) מגמת נוכחות (trend)
  let trend = "no_change";
  if (presencePercents.length > 2) {
    // Simple trend: compare average of first third and last third
    const n = presencePercents.length;
    const firstAvg =
      presencePercents.slice(0, Math.ceil(n / 3)).reduce((a, b) => a + b, 0) /
      Math.ceil(n / 3);
    const lastAvg =
      presencePercents
        .slice(Math.floor((n * 2) / 3))
        .reduce((a, b) => a + b, 0) /
      (n - Math.floor((n * 2) / 3));
    if (lastAvg - firstAvg > 5) trend = "up";
    else if (firstAvg - lastAvg > 5) trend = "down";
  }
  // Trend icon and color
  let trendIconComponent = <FaMinus style={{ fontSize: 32 }} />;
  let trendColor = "#1976d2";
  if (trend === "up") {
    trendIconComponent = <FaArrowUp style={{ fontSize: 32 }} />;
    trendColor = "#43a047";
  } else if (trend === "down") {
    trendIconComponent = <FaArrowDown style={{ fontSize: 32 }} />;
    trendColor = "#e53935";
  }
  // --- END STATISTICS SECTION LOGIC ---

  // Status chip style
  const chip = (text, color) => (
    <span
      style={{
        display: "inline-block",
        background: color,
        color: "#fff",
        borderRadius: 12,
        padding: "2px 12px",
        fontWeight: 600,
        fontSize: "0.98em",
        marginRight: 6,
        marginLeft: 6,
        verticalAlign: "middle",
      }}
    >
      {text}
    </span>
  );

  const titleStyle = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "#1976d2",
    borderRadius: 12,
    padding: "10px 18px",
    color: "#fff",
    fontWeight: 900,
    fontSize: "1.5rem",
    boxShadow: "0 2px 8px #1976d233",
  };
  const infoBlock = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
    flex: 1,
    fontWeight: 500,
    fontSize: "1.08em",
    color: "#222",
    background: "#f8fafc",
    borderRadius: 10,
    padding: "12px 16px",
    boxShadow: "0 1px 4px #1976d21a",
    margin: 0,
    minHeight: 48,
    maxWidth: "100%",
    overflow: "hidden",
  };
  const labelStyle = {
    color: "#1976d2",
    fontWeight: 700,
    marginLeft: 4,
    whiteSpace: "nowrap",
  };
  const valueStyle = {
    color: "#222",
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };
  const descriptionStyle = {
    width: "100%",
    background: "#f8fafc",
    borderRadius: 10,
    padding: "12px 16px",
    fontWeight: 500,
    fontSize: "1.08em",
    minHeight: 48,
    maxHeight: 120,
    overflowY: "auto",
    wordBreak: "break-word",
    color: "#222",
    marginBottom: 18,
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
  };
  const linkChip = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "#1976d2",
    color: "#fff",
    borderRadius: 16,
    padding: "7px 18px",
    fontWeight: 700,
    fontSize: "1.08em",
    marginBottom: 18,
    marginTop: 2,
    textDecoration: "none",
    boxShadow: "0 2px 8px #1976d233",
    transition: "background 0.2s",
    width: "fit-content",
  };
  const sectionTitleStyle = {
    fontWeight: 700,
    fontSize: "1.35rem",
    color: "#1976d2",
    borderRight: "6px solid #1976d2",
    paddingRight: 16,
    marginBottom: 18,
    display: "flex",
    alignItems: "center",
    gap: 10,
    direction: "rtl",
  };
  const dividerStyle = {
    borderTop: "1.5px solid #e3e9f7",
    margin: "24px 0 18px 0",
    width: "100%",
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      width={70}
      title={
        <div style={titleStyle}>
          <FaCalendarAlt size={28} style={{ flexShrink: 0 }} />
          <span style={{ direction: "rtl" }}>
            {`קבוצת ${group.name} בסניף ${group.dojo}`}
          </span>
        </div>
      }
      children={
        <div
          className="hebrewText"
          style={{
            direction: "rtl",
            fontSize: "1.1rem",
            color: "#222",
            minWidth: 320,
            maxWidth: 1100,
            margin: "0 auto",
            width: "100%",
          }}
        >
          {/* Group registration link copy button at the top */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 10,
            }}
          >
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="copy-tooltip">
                  {copyButtonState.isCopied ? "הועתק!" : "העתק קישור"}
                </Tooltip>
              }
            >
              <Button
                variant={copyButtonState.isCopied ? "success" : "secondary"}
                onClick={() => onCopyLink(group)}
                style={{
                  width: "auto",
                  minWidth: 180,
                  transition: "all 0.3s ease",
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  borderRadius: 8,
                  boxShadow: copyButtonState.isCopied
                    ? "0 0 0 4px #43a04744"
                    : "0 2px 8px #1976d233",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  outline: copyButtonState.isCopied
                    ? "2px solid #43a047"
                    : undefined,
                  animation: copyButtonState.isCopied
                    ? "pulse 0.5s"
                    : undefined,
                }}
              >
                <span>{copyButtonState.icon}</span>
                העתק קישור הרשמה לקבוצה
              </Button>
            </OverlayTrigger>
          </div>
          {/* --- Section: Group Details --- */}
          <div style={dividerStyle} />
          <div style={sectionTitleStyle}>
            <FaInfoCircle className="modern-section-header-icon" />
            פרטי הקבוצה
          </div>
          {/* First row: group name and branch name */}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 24,
              width: "100%",
              marginBottom: 18,
              flexWrap: "wrap",
            }}
          >
            <div style={infoBlock}>
              <FaInfoCircle color="#1976d2" size={20} />
              <span style={labelStyle}>שם הקבוצה:</span>
              <span style={valueStyle}>{group.name}</span>
            </div>
            <div style={infoBlock}>
              <FaMapMarkerAlt color="#1976d2" size={20} />
              <span style={labelStyle}>שם הסניף:</span>
              <span style={valueStyle}>{group.dojo}</span>
            </div>
          </div>
          {/* Second row: athlete counter and payment check */}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 24,
              width: "100%",
              marginBottom: 18,
              flexWrap: "wrap",
            }}
          >
            <div style={infoBlock}>
              <FaUserFriends color="#1976d2" size={20} />
              <span style={labelStyle}>ספורטאים:</span>
              {chip(groupSize, "#1976d2")}
            </div>
            <div style={infoBlock}>
              <FaMoneyCheckAlt color="#1976d2" size={20} />
              <span style={labelStyle}>בדיקת תשלום:</span>
              {chip(
                group.paymentCheck ? "כן" : "לא",
                group.paymentCheck ? "#43a047" : "#e53935"
              )}
            </div>
          </div>
          {group.description && (
            <div style={descriptionStyle}>
              <FaInfoCircle
                color="#1976d2"
                size={20}
                style={{ marginTop: 2 }}
              />
              <span style={{ ...valueStyle, whiteSpace: "pre-line" }}>
                {group.description}
              </span>
            </div>
          )}
          {group.paymentUrl && (
            <a
              href={group.paymentUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={linkChip}
            >
              <FaLink size={18} />
              דף התשלום לקבוצה
            </a>
          )}
          {/* --- Section: Training Days --- */}
          <div style={dividerStyle} />
          <div style={sectionTitleStyle}>
            <FaCalendarAlt className="modern-section-header-icon" />
            ימי ושעות אימון
          </div>
          <div style={{ marginBottom: 10 }}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 12,
                marginTop: 6,
              }}
            >
              {group.trainingSlots.map((slot, index) => (
                <div
                  key={index}
                  style={{
                    background: "#e3e9f7",
                    borderRadius: 8,
                    padding: "6px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    fontWeight: 500,
                    fontSize: "1rem",
                    color: "#1976d2",
                    width: "fit-content",
                  }}
                >
                  <span style={{ minWidth: 90 }}>
                    {practiceDays.find((d) => d.id === slot.day)?.label}
                  </span>
                  <span
                    style={{
                      color: "#333",
                      fontWeight: 400,
                      fontSize: "0.98em",
                    }}
                  >
                    {slot.range}
                  </span>
                </div>
              ))}
            </div>
          </div>
          {/* --- STATISTICS SECTION --- */}
          <div style={dividerStyle} />
          {/* Stats Title */}
          <div style={sectionTitleStyle}>
            <FaChartLine className="modern-section-header-icon" />
            סטטיסטיקות מהחודש האחרון
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 24,
              width: "100%",
              marginBottom: 18,
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            {/* Card: Trainings This Month */}
            <div
              style={{
                background: "#fff",
                borderRadius: 16,
                boxShadow: "0 2px 12px #1976d233",
                padding: "18px 28px",
                minWidth: 160,
                minHeight: 180,
                height: 180,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 0,
                border: "2px solid #e3e9f7",
                position: "relative",
              }}
            >
              <StatTooltip
                text="מספר דיווחי הנוכחות שהוזנו בחודש האחרון"
                style={{ position: "absolute", top: 10, right: 10, zIndex: 1 }}
              >
                <FaInfoCircle
                  size={16}
                  style={{
                    color: "#1976d2",
                    cursor: "pointer",
                    background: "#fff",
                    borderRadius: "50%",
                  }}
                />
              </StatTooltip>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 8,
                }}
              >
                <FaCalendarAlt size={28} style={{ color: "#1976d2" }} />
              </div>
              <div
                style={{
                  fontWeight: 900,
                  fontSize: "2.1rem",
                  color: "#1976d2",
                  marginBottom: 2,
                }}
              >
                {trainingsThisMonth}
              </div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "1.08em",
                  color: "#1976d2",
                }}
              >
                אימונים שבוצעו
              </div>
            </div>
            {/* Card: New Students Joined */}
            {(() => {
              const isAnyNew = newStudentsCount > 0;
              const cardColor = isAnyNew ? "#43a047" : "#1976d2";
              const boxShadow = isAnyNew
                ? "0 2px 12px #43a04733"
                : "0 2px 12px #1976d233";
              const border = isAnyNew
                ? "2px solid #43a04733"
                : "2px solid #1976d233";
              return (
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 16,
                    boxShadow: boxShadow,
                    padding: "18px 28px",
                    minWidth: 160,
                    minHeight: 180,
                    height: 180,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 0,
                    border: border,
                    position: "relative",
                  }}
                >
                  <StatTooltip
                    text="מספר הספורטאים שהצטרפו לקבוצה בחודש האחרון"
                    style={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      zIndex: 1,
                    }}
                  >
                    <FaInfoCircle
                      size={16}
                      style={{
                        color: cardColor,
                        cursor: "pointer",
                        background: "#fff",
                        borderRadius: "50%",
                      }}
                    />
                  </StatTooltip>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginBottom: 8,
                    }}
                  >
                    <FaStar size={28} style={{ color: cardColor }} />
                  </div>
                  <div
                    style={{
                      fontWeight: 900,
                      fontSize: "2.1rem",
                      color: cardColor,
                      marginBottom: 2,
                    }}
                  >
                    {newStudentsCount}
                  </div>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: "1.08em",
                      color: "#1976d2",
                    }}
                  >
                    ספורטאים שהצטרפו
                  </div>
                </div>
              );
            })()}
            {/* Card: Average Presence Percent */}
            {(() => {
              let avgColor = "#43a047";
              if (avgPresencePercent < 50) avgColor = "#e53935";
              else if (avgPresencePercent < 75) avgColor = "#ff9800";
              return (
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 16,
                    boxShadow:
                      validLogsCount > 0
                        ? `0 2px 12px ${avgColor}33`
                        : "0 2px 12px #1976d233",
                    padding: "18px 28px",
                    minWidth: 160,
                    minHeight: 180,
                    height: 180,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 0,
                    border:
                      validLogsCount > 0
                        ? `2px solid ${avgColor}33`
                        : "2px solid #1976d233",
                    position: "relative",
                  }}
                >
                  <StatTooltip
                    text="אחוז הנוכחות הממוצע בכלל דיווחי הנוכחות בחודש האחרון"
                    style={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      zIndex: 1,
                    }}
                  >
                    <FaInfoCircle
                      size={16}
                      style={{
                        color: validLogsCount > 0 ? avgColor : "#1976d2",
                        cursor: "pointer",
                        background: "#fff",
                        borderRadius: "50%",
                      }}
                    />
                  </StatTooltip>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginBottom: 8,
                    }}
                  >
                    <FaUserFriends
                      size={28}
                      style={{
                        color: validLogsCount > 0 ? avgColor : "#1976d2",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      fontWeight: 900,
                      fontSize: "2.1rem",
                      color: validLogsCount > 0 ? avgColor : "#1976d2",
                      marginBottom: 2,
                    }}
                  >
                    {validLogsCount > 0 ? `${avgPresencePercent}%` : "-"}
                  </div>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: "1.08em",
                      color: "#1976d2",
                    }}
                  >
                    אחוז נוכחות ממוצע
                  </div>
                </div>
              );
            })()}
            {/* Card: Trend */}
            <div
              style={{
                background: "#fff",
                borderRadius: 16,
                boxShadow: `0 2px 12px ${trendColor}33`,
                padding: "18px 28px",
                minWidth: 160,
                minHeight: 180,
                height: 180,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 0,
                border: `2px solid ${trendColor}33`,
                position: "relative",
              }}
            >
              <StatTooltip
                text="מגמת השינוי באחוז הנוכחות באימונים בחודש האחרון"
                style={{ position: "absolute", top: 10, right: 10, zIndex: 2 }}
              >
                <FaInfoCircle
                  size={16}
                  style={{
                    color: "#1976d2",
                    cursor: "pointer",
                    background: "#fff",
                    borderRadius: "50%",
                  }}
                />
              </StatTooltip>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 8,
                }}
              >
                <span style={{ color: trendColor }}>{trendIconComponent}</span>
              </div>
              <div
                style={{
                  fontWeight: 900,
                  fontSize: "1.5rem",
                  color: trendColor,
                  marginBottom: 2,
                }}
              >
                {trend === "up"
                  ? "מגמת עלייה"
                  : trend === "down"
                    ? "מגמת ירידה"
                    : "ללא שינוי"}
              </div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "1.08em",
                  color: "#1976d2",
                }}
              >
                מגמת נוכחות
              </div>
            </div>
          </div>
          {/* --- Section: Group Students --- */}
          <div style={dividerStyle} />
          <div style={sectionTitleStyle}>
            <FaUserFriends className="modern-section-header-icon" />
            ספורטאים בקבוצה
          </div>
          <div style={{ paddingTop: 8 }}>
            <DataTable
              data={groupStudents}
              type="students"
              columnMapping={{
                name: "שם החניך",
                age: "גיל",
                address: "כתובת",
                gender: "מין",
                ...(userData?.userField === "martial_arts" && {
                  beltLevel: "חגורה",
                  weightKG: "משקל (ק״ג)",
                }),
                ...(userData?.userField === "ball_games" && {
                  shirtSize: "מידת חולצה",
                  pantsSize: "מידת מכנסיים",
                }),
              }}
              ignoredAttributes={[
                "displayGroups",
                "groupsIDs",
                "id",
                "specialFields",
                "beltColor",
                "birthday",
                "classPresenceRecords",
                "firstParentEmailAddress",
                "firstParentName",
                "firstParentNumber",
                "imageURI",
                "imageUploadTime",
                "status",
                "weight",
                "secondParentEmailAddress",
                "secondParentName",
                "secondParentNumber",
                "momPhoneNumber",
                "momEmailAddress",
                "paymentCheckData",
                "IDnumber",
                "studentID",
                "studentCreatedTime",
                "studentSource",
                "createdAt",
                "studentCreatedAt",
                "groupsIDs",
                "formattedCreatedAt",
                ...(userData?.userField === "martial_arts"
                  ? ["shirtSize", "pantsSize", "shortsSize"]
                  : []),
                ...(userData?.userField === "ball_games"
                  ? ["beltLevel", "weightKG"]
                  : []),
              ]}
              metaData={groups || []}
              messageIfEmpty="אין ספורטאים בקבוצה זו"
            />
          </div>
          <style>{`
                        @keyframes pulse {
                            0% { box-shadow: 0 0 0 0 #43a04744; }
                            70% { box-shadow: 0 0 0 8px #43a04722; }
                            100% { box-shadow: 0 0 0 0 #43a04744; }
                        }
                    `}</style>
        </div>
      }
      footerButtons={
        hasEditPrivilege
          ? [
            {
              title: "מחיקה",
              onClick: () => onDelete(group),
              style: "red",
            },
            {
              title: "עריכה",
              onClick: () => onEdit(group),
              style: "warning",
            },
          ]
          : []
      }
    />
  );
}

export default GroupDetailsModal;
